import { PaginatedResponse } from "@/interfaces/paginatedResponse";
import { Chain } from "@/internal/modules/providers/interfaces/Chain";
import axios from "@/lib/axios";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { ref } from "vue";
import { ToastInterface, useToast } from "vue-toastification";

interface Props {
  immediate?: boolean;
}

interface Params {
  page?: number;
}

interface CreateParams {
  name: string;
}

interface CreateMediaParams {
  id: number | string;
  collection: string;
  media: File;
}

interface ChainBrand {
  chainId: number;
  name: string;
}

interface UpdateParams {
  id: number;
  name: string;
}

const getChains = async (params: Params): Promise<PaginatedResponse<Chain>> => {
  const { data } = await axios.get(`/api/internal/chains?page=${params.page}`);

  return data;
};

const createChain = async (params: CreateParams) => {
  const { data } = await axios.post(`/api/internal/chains`, params);

  return data;
};

const createChainMedia = async (params: CreateMediaParams) => {
  const { collection, media } = params;
  const formData = new FormData();
  formData.append("media", media);
  formData.append("collection", collection);

  const { data } = await axios.post(
    `/api/internal/chains/${params.id}/medias`,
    formData,
  );

  return data;
};

const createChainBrand = async (params: ChainBrand, toast: ToastInterface) => {
  const urlChain = `/api/internal/chains/${params.chainId}/brands`;
  const urlBrad = `/api/internal/brands`;

  try {
    const { data } = await axios.post(
      `${params.chainId ? urlChain : urlBrad}`,
      {
        name: params.name,
      },
    );

    toast.success("Cadena creada correctamente");

    return data;
  } catch (error: any) {
    console.log(error.response.data.message);

    toast.error(error.response.data.message);
  }
};

const updateChain = async (params: UpdateParams, toast: ToastInterface) => {
  try {
    const { data } = await axios.put(`/api/internal/chains/${params.id}`, {
      name: params.name,
    });

    toast.success("Cadena actualizada correctamente");

    return data;
  } catch (error: any) {
    toast.error(error.response.data.message);
  }
};

const deleteChain = async (id: number, toast: ToastInterface) => {
  try {
    const { data } = await axios.delete(`/api/internal/chains/${id}`);

    toast.success("Cadena desactivada correctamente");
    return data;
  } catch (error: any) {
    toast.error(error.response.data.message);
  }
};

const restoreChain = async (id: number, toast: ToastInterface) => {
  try {
    const { data } = await axios.post(`/api/internal/chains/${id}/restore`);

    toast.success("Cadena activada correctamente");
    return data;
  } catch (error: any) {
    toast.error(error.response.data.message);
  }
};

const useChains = (props: Props) => {
  const { immediate = true } = props;
  const toast = useToast();
  const page = ref<number | string>(1);

  const { data, isLoading, refetch } = useQuery(
    ["chains"],
    () =>
      getChains({
        page: Number(page.value),
      }),
    {
      enabled: immediate,
    },
  );

  const createMutation = useMutation((params: CreateParams) =>
    createChain(params),
  );

  const createMutationMedia = useMutation((params: CreateMediaParams) =>
    createChainMedia(params),
  );

  const createMutationBrand = useMutation((params: ChainBrand) =>
    createChainBrand(params, toast),
  );

  const updateChainMutation = useMutation((params: UpdateParams) =>
    updateChain(params, toast),
  );

  const deleteChainMutation = useMutation((id: number) =>
    deleteChain(id, toast),
  );

  const restoreChainMutation = useMutation((id: number) =>
    restoreChain(id, toast),
  );

  return {
    data,
    isLoading,
    refetch,
    createChain,

    createMutation,
    updateChainMutation,
    createChainLoading: createMutation.isLoading,
    createChainError: createMutation.error,
    createChainSuccess: createMutation.isSuccess,
    createChainMutate: createMutation.mutate,
    createChainAsync: createMutation.mutateAsync,

    createChainMediaLoading: createMutationMedia.isLoading,
    createChainMediaError: createMutationMedia.error,
    createChainMediaSuccess: createMutationMedia.isSuccess,
    createChainMediaMutate: createMutationMedia.mutate,
    createChainMediaAsync: createMutationMedia.mutateAsync,

    createMutationBrand,
    createChainBrandLoading: createMutationBrand.isLoading,
    createChainBrandError: createMutationBrand.error,
    createChainBrandSuccess: createMutationBrand.isSuccess,
    createChainBrandMutate: createMutationBrand.mutate,
    createChainBrandAsync: createMutationBrand.mutateAsync,

    updateChainLoading: updateChainMutation.isLoading,
    updateChainError: updateChainMutation.error,
    updateChainSuccess: updateChainMutation.isSuccess,
    updateChainMutate: updateChainMutation.mutate,
    updateChainAsync: updateChainMutation.mutateAsync,

    deleteChainLoading: deleteChainMutation.isLoading,
    deleteChainError: deleteChainMutation.error,
    deleteChainSuccess: deleteChainMutation.isSuccess,
    deleteChainMutate: deleteChainMutation.mutate,
    deleteChainAsync: deleteChainMutation.mutateAsync,

    restoreChainLoading: restoreChainMutation.isLoading,
    restoreChainError: restoreChainMutation.error,
    restoreChainSuccess: restoreChainMutation.isSuccess,
    restoreChainMutate: restoreChainMutation.mutate,
    restoreChainAsync: restoreChainMutation.mutateAsync,
  };
};

export default useChains;
